// Pace-to-goal for the current quarter (handoff 5.5, one-source variant:
// targets live in content_quarters.kpi_targets, set on the Content Plan).
// Actuals come from GA4 QTD (sessions, booked calls), AC (subscribers,
// approximated by the 30d window when the quarter is young), and the
// content log (published anchors). "Expected" prorates the target by how
// much of the quarter has elapsed; behind = actual < 85% of expected.

import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../../utils/apiFetch';
import { useContent } from '../../../context/ContentContext';
import { quarterOf } from '../stages';
import { rangeWindow } from '../dates';

const BEHIND_TOLERANCE = 0.85;

export function usePace() {
  const { entries } = useContent();
  const quarter = quarterOf();
  const [remote, setRemote] = useState({ plan: null, funnel: null, traffic: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [planRes, funnelRes, trafficRes] = await Promise.all([
          apiFetch(`/api/data/content-plan/${quarter}`).then(r => r.json()),
          apiFetch('/api/gsc/ga4/funnel?range=qtd').then(r => r.json()).catch(() => null),
          apiFetch('/api/gsc/ga4/traffic?range=qtd').then(r => r.json()).catch(() => null),
        ]);
        if (!cancelled) {
          setRemote({
            plan: planRes?.plan || null,
            funnel: funnelRes?.configured === false ? null : funnelRes,
            traffic: trafficRes?.configured === false ? null : trafficRes,
            loading: false,
          });
        }
      } catch {
        if (!cancelled) setRemote(r => ({ ...r, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [quarter]);

  return useMemo(() => {
    const targets = remote.plan?.kpiTargets || {};
    if (remote.loading || Object.keys(targets).length === 0) {
      return { quarter, loading: remote.loading, items: [] };
    }

    const { start, end } = rangeWindow('qtd');
    const qStart = start;
    const qEnd = new Date(qStart.getFullYear(), qStart.getMonth() + 3, 0);
    const elapsed = Math.min(Math.max((end - qStart) / (qEnd - qStart), 0), 1);

    const anchorsPublished = entries.filter(e =>
      e.quarter === quarter && (e.channel || 'blog') === 'blog' && e.status === 'published'
    ).length;

    const ACTUALS = {
      sessions: remote.traffic?.totals?.sessions ?? null,
      booked_calls: remote.funnel?.calls ?? null,
      anchors: anchorsPublished,
      subscribers: null, // no QTD window from AC yet; skipped unless targeted
    };
    const LABELS = {
      sessions: 'Sessions', booked_calls: 'Booked calls',
      anchors: 'Blog anchors', subscribers: 'New subscribers',
    };

    const items = Object.entries(targets)
      .filter(([metric, target]) => Number(target) > 0 && ACTUALS[metric] !== null && ACTUALS[metric] !== undefined)
      .map(([metric, target]) => {
        const actual = ACTUALS[metric];
        const expected = Number(target) * elapsed;
        return {
          metric,
          label: LABELS[metric] || metric,
          target: Number(target),
          actual,
          expected,
          behind: actual < expected * BEHIND_TOLERANCE,
        };
      });

    return { quarter, loading: false, elapsed, items };
  }, [remote, entries, quarter]);
}
